import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, Lightbulb, MessageCircle, X, Upload, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  closeFeedbackDialog,
  useFeedbackDialogOpen,
} from "@/hooks/use-feedback-dialog";

type FeedbackType = "bug" | "idea" | "general";
type FeedbackModule =
  | "general"
  | "deals"
  | "score_results"
  | "sensitivity"
  | "market_insights"
  | "documents"
  | "copilot"
  | "reports"
  | "admin";

const MODULE_OPTIONS: { value: FeedbackModule; label: string }[] = [
  { value: "general", label: "General" },
  { value: "deals", label: "Deals" },
  { value: "score_results", label: "Score Results" },
  { value: "sensitivity", label: "Sensitivity" },
  { value: "market_insights", label: "Market Insights" },
  { value: "documents", label: "Documents" },
  { value: "copilot", label: "Copilot" },
  { value: "reports", label: "Reports" },
  { value: "admin", label: "Admin" },
];

const MAX_ATTACHMENTS = 3;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp"];

function inferModule(pathname: string): FeedbackModule {
  if (/^\/deals-v2\/[^/]+\/results/.test(pathname)) return "score_results";
  if (/^\/deals-v2\/[^/]+\/sensitivity/.test(pathname)) return "sensitivity";
  if (/^\/deals-v2\/[^/]+\/negotiation/.test(pathname)) return "deals";
  if (/^\/deals-v2\/[^/]+\/export/.test(pathname)) return "reports";
  if (/^\/deals-v2\/reports/.test(pathname)) return "reports";
  if (/^\/deals-v2/.test(pathname)) return "deals";
  if (/^\/market-insights/.test(pathname)) return "market_insights";
  if (/^\/reports/.test(pathname)) return "reports";
  if (/^\/admin/.test(pathname)) return "admin";
  return "general";
}

function safeFilename(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 80);
}

export function FeedbackDialog() {
  const open = useFeedbackDialogOpen();
  const location = useLocation();
  const inferred = useMemo(() => inferModule(location.pathname), [location.pathname]);

  const [type, setType] = useState<FeedbackType>("bug");
  const [module, setModule] = useState<FeedbackModule>(inferred);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset module when opening (respect user override during a session)
  useEffect(() => {
    if (open) setModule(inferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Manage object URL previews
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const resetForm = () => {
    setType("bug");
    setTitle("");
    setMessage("");
    setFiles([]);
    setModule(inferred);
  };

  const handleClose = (nextOpen: boolean) => {
    if (submitting) return;
    if (!nextOpen) closeFeedbackDialog();
  };

  const handleFilesSelected = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    const accepted: File[] = [];
    for (const f of incoming) {
      if (!ALLOWED_MIME.includes(f.type)) {
        toast.error(`${f.name}: only PNG, JPG or WEBP images.`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name}: exceeds 5MB limit.`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Add a title and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData.user) throw new Error("You must be signed in.");
      const user = userData.user;

      // Upload attachments
      const paths: string[] = [];
      for (const file of files) {
        const path = `${user.id}/${Date.now()}_${safeFilename(file.name)}`;
        const { error: upErr } = await supabase.storage
          .from("feedback-attachments")
          .upload(path, file, { cacheControl: "3600", upsert: false });
        if (upErr) throw upErr;
        paths.push(path);
      }

      // Optional deal context from sessionStorage
      const dealId = sessionStorage.getItem("brickshare_deal_id") || null;
      const dealName = sessionStorage.getItem("brickshare_deal_name") || null;

      const appVersion =
        (import.meta.env.VITE_APP_VERSION as string | undefined) || null;

      const { error: insertErr } = await supabase.from("feedback").insert({
        user_id: user.id,
        email: user.email ?? null,
        type,
        module,
        title: title.trim().slice(0, 120),
        message: message.trim(),
        attachments: paths,
        path: window.location.pathname,
        user_agent: navigator.userAgent,
        app_version: appVersion,
        deal_id: dealId,
        deal_name: dealName,
      } as never);
      if (insertErr) throw insertErr;

      toast.success("Thanks — we read every piece of feedback.");
      resetForm();
      closeFeedbackDialog();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not send feedback.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Send feedback</DialogTitle>
          <DialogDescription>
            Bugs, ideas, or anything on your mind. We read every one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Type
            </Label>
            <ToggleGroup
              type="single"
              value={type}
              onValueChange={(v) => v && setType(v as FeedbackType)}
              className="justify-start gap-2"
            >
              <ToggleGroupItem value="bug" className="gap-1.5 data-[state=on]:bg-accent/20">
                <Bug className="w-3.5 h-3.5" /> Bug
              </ToggleGroupItem>
              <ToggleGroupItem value="idea" className="gap-1.5 data-[state=on]:bg-accent/20">
                <Lightbulb className="w-3.5 h-3.5" /> Idea
              </ToggleGroupItem>
              <ToggleGroupItem value="general" className="gap-1.5 data-[state=on]:bg-accent/20">
                <MessageCircle className="w-3.5 h-3.5" /> General
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Module */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Area
            </Label>
            <Select value={module} onValueChange={(v) => setModule(v as FeedbackModule)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODULE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="fb-title" className="text-xs uppercase tracking-wide text-muted-foreground">
              Title
            </Label>
            <Input
              id="fb-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Short summary"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="fb-message" className="text-xs uppercase tracking-wide text-muted-foreground">
              Details
            </Label>
            <Textarea
              id="fb-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="What happened? What did you expect?"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Screenshots{" "}
              <span className="text-muted-foreground/70 normal-case tracking-normal">
                (optional, up to {MAX_ATTACHMENTS})
              </span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {previews.map((src, i) => (
                <div
                  key={src}
                  className="relative w-16 h-16 rounded-md overflow-hidden border border-border bg-muted"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-0.5 right-0.5 rounded-full bg-background/90 p-0.5 border border-border hover:bg-background"
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {files.length < MAX_ATTACHMENTS && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-16 h-16 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                >
                  <Upload className="w-4 h-4" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="hidden"
                onChange={(e) => handleFilesSelected(e.target.files)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => closeFeedbackDialog()}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…
              </>
            ) : (
              "Send feedback"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
